import { useEffect, useMemo, useState } from 'react';
import { getGradeLabel } from '@car-pool/shared/schoolRecords';
import { apiFetch } from '../config/api';
import PhotoUploadField from '../components/PhotoUploadField';

const STATUSES = ['active', 'inactive', 'graduate'];
const GENDERS = ['M', 'F'];
const EMERGENCY_RELATIONSHIPS = ['Mother', 'Father', 'Guardian', 'Sibling', 'Relative', 'Friend', 'Other'];

function buildStudentForm(student) {
  if (!student) {
    return {
      f_name: '', l_name: '', dob: '', grade: '', gender: 'M', status: 'active',
      grade_split_id: '',
      student_email: '', student_phone: '',
      street_address: '', city: '', state: '', zip_code: '',
      emergency_contact_f_name: '', emergency_contact_l_name: '',
      emergency_contact_phone: '', emergency_contact_relationship: '',
      photo_relative_path: null,
    };
  }

  return {
    f_name: student.f_name || '',
    l_name: student.l_name || '',
    dob: student.dob?.slice(0, 10) || '',
    grade: String(student.grade ?? ''),
    grade_split_id: student.grade_split_id ? String(student.grade_split_id) : '',
    gender: student.gender || 'M',
    status: student.status || 'active',
    student_email: student.student_email || '',
    student_phone: student.student_phone || '',
    street_address: student.street_address || '',
    city: student.city || '',
    state: student.state || '',
    zip_code: student.zip_code || '',
    emergency_contact_f_name: student.emergency_contact_f_name || '',
    emergency_contact_l_name: student.emergency_contact_l_name || '',
    emergency_contact_phone: student.emergency_contact_phone || '',
    emergency_contact_relationship: student.emergency_contact_relationship || '',
    photo_relative_path: student.photo_relative_path || null,
  };
}

function isActive(value) {
  return value === true || value === 1 || String(value) === '1';
}

export default function StudentForm({ token, navigate, schoolId, student, studentId }) {
  const editStudentId = student?.id || studentId || null;
  const isEdit = !!editStudentId;
  const [studentRecord, setStudentRecord] = useState(student || null);
  const effectiveSchoolId = schoolId || student?.school_id || studentRecord?.school_id;
  const recordSchoolMismatch = Boolean(
    studentRecord?.school_id
    && effectiveSchoolId
    && Number(studentRecord.school_id) !== Number(effectiveSchoolId)
  );

  const [form, setForm] = useState(() => buildStudentForm(student));
  const [gradeNames, setGradeNames] = useState([]);
  const [gradeSplits, setGradeSplits] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit && !student);

  useEffect(() => {
    if (!student) return;
    setStudentRecord(student);
    setForm(buildStudentForm(student));
    setLoading(false);
  }, [student]);

  useEffect(() => {
    if (!effectiveSchoolId) return undefined;

    let cancelled = false;

    Promise.all([
      apiFetch(`/school-grade-names?school_id=${effectiveSchoolId}`, {}, token).catch(() => ({ data: [] })),
      apiFetch(`/school-grade-splits?school_id=${effectiveSchoolId}`, {}, token).catch(() => ({ data: [] })),
    ]).then(([gradeNamesRes, gradeSplitsRes]) => {
      if (cancelled) return;
      setGradeNames((gradeNamesRes.data || []).filter((grade) => isActive(grade.is_active)));
      setGradeSplits(gradeSplitsRes.data || []);
    });

    return () => {
      cancelled = true;
    };
  }, [effectiveSchoolId, token]);

  useEffect(() => {
    if (!isEdit || student || !editStudentId) return undefined;

    let cancelled = false;
    setLoading(true);

    apiFetch(`/students/${editStudentId}`, {}, token)
      .then((res) => {
        if (cancelled) return;
        setStudentRecord(res.data);
        setForm(buildStudentForm(res.data));
      })
      .catch((err) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [editStudentId, isEdit, student, token]);

  const set = (field) => (e) => setForm((currentForm) => ({ ...currentForm, [field]: e.target.value }));
  const setGrade = (e) => {
    const nextGrade = e.target.value;
    setForm((currentForm) => ({
      ...currentForm,
      grade: nextGrade,
      grade_split_id: '',
    }));
  };

  const gradeOptions = useMemo(() => {
    const named = gradeNames
      .map((grade) => ({
        gradeCode: Number(grade.grade_code),
        label: grade.label || getGradeLabel(grade.grade_code),
      }))
      .filter((grade) => Number.isInteger(grade.gradeCode));

    const currentGrade = Number(form.grade);
    if (named.length) {
      const byGrade = new Map(named.map((grade) => [grade.gradeCode, grade]));
      if (Number.isInteger(currentGrade) && !byGrade.has(currentGrade)) {
        byGrade.set(currentGrade, { gradeCode: currentGrade, label: getGradeLabel(currentGrade) });
      }
      return [...byGrade.values()].sort((left, right) => left.gradeCode - right.gradeCode);
    }

    return Array.from({ length: 16 }, (_, index) => index - 3)
      .map((gradeCode) => ({ gradeCode, label: getGradeLabel(gradeCode) }));
  }, [form.grade, gradeNames]);

  const availableSplits = useMemo(() => {
    const selectedGrade = Number(form.grade);
    if (!Number.isInteger(selectedGrade)) return [];

    return gradeSplits
      .filter((split) => Number(split.grade) === selectedGrade)
      .sort((left, right) => {
        const activeDiff = Number(isActive(right.is_active)) - Number(isActive(left.is_active));
        if (activeDiff !== 0) return activeDiff;
        return String(left.split_code || '').localeCompare(String(right.split_code || ''));
      });
  }, [form.grade, gradeSplits]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (recordSchoolMismatch) {
      setError('This student does not belong to the selected school.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        school_id: effectiveSchoolId,
        f_name: form.f_name,
        l_name: form.l_name,
        dob: form.dob || null,
        grade: parseInt(form.grade),
        grade_split_id: form.grade_split_id ? parseInt(form.grade_split_id) : null,
        gender: form.gender,
        status: form.status,
        student_email: form.student_email || null,
        student_phone: form.student_phone || null,
        street_address: form.street_address || null,
        city: form.city || null,
        state: form.state || null,
        zip_code: form.zip_code || null,
        emergency_contact_f_name: form.emergency_contact_f_name || null,
        emergency_contact_l_name: form.emergency_contact_l_name || null,
        emergency_contact_phone: form.emergency_contact_phone || null,
        emergency_contact_relationship: form.emergency_contact_relationship || null,
        photo_relative_path: form.photo_relative_path || null,
      };

      if (isEdit) {
        await apiFetch(`/students/${studentRecord?.id || editStudentId}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        }, token);
      } else {
        await apiFetch('/students', {
          method: 'POST',
          body: JSON.stringify(body),
        }, token);
      }
      navigate('students', effectiveSchoolId);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="sa-empty">Loading...</div>;
  if (isEdit && !studentRecord) return <div className="sa-error">Student not found</div>;
  if (recordSchoolMismatch) return <div className="sa-error">Student not found for this school</div>;

  return (
    <div>
      <div className="sa-breadcrumb">
        <button onClick={() => navigate('dashboard')}>Schools</button>
        {' / '}
        <button onClick={() => navigate('school-detail', effectiveSchoolId)}>School</button>
        {' / '}
        <button onClick={() => navigate('students', effectiveSchoolId)}>Students</button>
        {' / '}
        {isEdit ? `Edit: ${studentRecord?.f_name || ''} ${studentRecord?.l_name || ''}`.trim() : 'Add Student'}
      </div>

      <div className="sa-card">
        <h2>{isEdit ? 'Edit Student' : 'Add Student'}</h2>

        {error && <div className="sa-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          <PhotoUploadField
            value={form.photo_relative_path}
            onChange={(path) => setForm((prev) => ({ ...prev, photo_relative_path: path }))}
            token={token}
          />

          <h3 style={{ fontSize: 15, color: '#555', marginBottom: 12 }}>Student Information</h3>
          <div className="sa-form-grid">
            <div className="sa-field">
              <label>First Name *</label>
              <input value={form.f_name} onChange={set('f_name')} required />
            </div>
            <div className="sa-field">
              <label>Last Name *</label>
              <input value={form.l_name} onChange={set('l_name')} required />
            </div>
            <div className="sa-field">
              <label>Date of Birth</label>
              <input type="date" value={form.dob} onChange={set('dob')} />
            </div>
            <div className="sa-field">
              <label>Grade *</label>
              <select value={form.grade} onChange={setGrade} required>
                <option value="">Select grade</option>
                {gradeOptions.map((grade) => (
                  <option key={grade.gradeCode} value={grade.gradeCode}>{grade.label}</option>
                ))}
              </select>
            </div>
            <div className="sa-field">
              <label>Grade Split</label>
              <select
                value={form.grade_split_id}
                onChange={set('grade_split_id')}
                disabled={!form.grade || availableSplits.length === 0}
              >
                <option value="">No split</option>
                {availableSplits.map((split) => (
                  <option key={split.id} value={split.id} disabled={!isActive(split.is_active)}>
                    {split.split_code}{isActive(split.is_active) ? '' : ' (inactive)'}
                  </option>
                ))}
              </select>
            </div>
            <div className="sa-field">
              <label>Gender *</label>
              <select value={form.gender} onChange={set('gender')}>
                {GENDERS.map((g) => <option key={g} value={g}>{g === 'M' ? 'Male' : 'Female'}</option>)}
              </select>
            </div>
            <div className="sa-field">
              <label>Status</label>
              <select value={form.status} onChange={set('status')}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="sa-field">
              <label>Email</label>
              <input type="email" value={form.student_email} onChange={set('student_email')} />
            </div>
            <div className="sa-field">
              <label>Phone</label>
              <input value={form.student_phone} onChange={set('student_phone')} />
            </div>
          </div>

          <h3 style={{ fontSize: 15, color: '#555', margin: '20px 0 12px' }}>Address</h3>
          <div className="sa-form-grid">
            <div className="sa-field full-width">
              <label>Street Address</label>
              <input value={form.street_address} onChange={set('street_address')} />
            </div>
            <div className="sa-field">
              <label>City</label>
              <input value={form.city} onChange={set('city')} />
            </div>
            <div className="sa-field">
              <label>State</label>
              <input value={form.state} onChange={set('state')} maxLength={2} placeholder="FL" />
            </div>
            <div className="sa-field">
              <label>ZIP Code</label>
              <input value={form.zip_code} onChange={set('zip_code')} />
            </div>
          </div>

          <h3 style={{ fontSize: 15, color: '#555', margin: '20px 0 12px' }}>Emergency Contact</h3>
          <div className="sa-form-grid">
            <div className="sa-field">
              <label>First Name</label>
              <input value={form.emergency_contact_f_name} onChange={set('emergency_contact_f_name')} />
            </div>
            <div className="sa-field">
              <label>Last Name</label>
              <input value={form.emergency_contact_l_name} onChange={set('emergency_contact_l_name')} />
            </div>
            <div className="sa-field">
              <label>Phone</label>
              <input value={form.emergency_contact_phone} onChange={set('emergency_contact_phone')} />
            </div>
            <div className="sa-field">
              <label>Relationship</label>
              <select value={form.emergency_contact_relationship} onChange={set('emergency_contact_relationship')}>
                <option value="">Select...</option>
                {EMERGENCY_RELATIONSHIPS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <div className="sa-btn-row">
            <button type="submit" className="sa-btn sa-btn-primary" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Update Student' : 'Create Student'}
            </button>
            <button type="button" className="sa-btn sa-btn-secondary" onClick={() => navigate('students', effectiveSchoolId)}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
